import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ExplainerVideo } from "@shared/schema";

interface ExplainerVideoButtonProps {
  pageKey: string;
  /**
   * When true, hide the "Explainer Video" text label below the `sm`
   * breakpoint so the button condenses to just its icon on phones.
   */
  compactOnMobile?: boolean;
}

function getLoomEmbedUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("loom.com")) {
      const path = parsed.pathname.replace("/share/", "/embed/").replace("/v/", "/embed/");
      return `https://www.loom.com${path}`;
    }
  } catch {
  }
  return url;
}

export function ExplainerVideoButton({ pageKey, compactOnMobile = false }: ExplainerVideoButtonProps) {
  const [open, setOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  const { data: video, isLoading } = useQuery<ExplainerVideo | null>({
    queryKey: ["/api/explainer-videos", pageKey],
    queryFn: async () => {
      const res = await fetch(`/api/explainer-videos/${pageKey}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    retry: false,
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div
        className={
          compactOnMobile
            ? "h-8 w-9 shrink-0 sm:w-[130px]"
            : "h-8 w-[130px] shrink-0"
        }
        aria-hidden="true"
      />
    );
  }

  if (!video || !video.embedUrl?.trim()) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setComingSoonOpen(true)}
          className="flex items-center gap-1.5 text-gray-400 border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-500 dark:text-gray-500 dark:border-gray-600 dark:bg-gray-800/50 dark:hover:bg-gray-700/50 dark:hover:border-gray-500 dark:hover:text-gray-400 shrink-0 cursor-pointer"
          data-testid={`button-explainer-video-${pageKey}`}
          title={compactOnMobile ? "Explainer Video" : undefined}
        >
          <PlayCircle className="h-4 w-4" />
          <span className={compactOnMobile ? "hidden sm:inline" : undefined}>
            Explainer Video
          </span>
        </Button>

        {comingSoonOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => setComingSoonOpen(false)}
            data-testid="explainer-video-coming-soon-modal-overlay"
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="explainer-video-coming-soon-modal"
            >
              <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">Explainer Video</h2>
                <button
                  onClick={() => setComingSoonOpen(false)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
                  data-testid="button-close-explainer-video-coming-soon"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <PlayCircle className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="text-explainer-video-coming-soon-title">Coming Soon</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="text-explainer-video-coming-soon-message">
                  An explainer video for this page is on its way. Check back soon!
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const embedUrl = getLoomEmbedUrl(video.embedUrl!);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-600 shrink-0"
        data-testid={`button-explainer-video-${pageKey}`}
        title={compactOnMobile ? "Explainer Video" : undefined}
      >
        <PlayCircle className="h-4 w-4" />
        <span className={compactOnMobile ? "hidden sm:inline" : undefined}>
          Explainer Video
        </span>
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)} data-testid="explainer-video-modal-overlay">
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="explainer-video-modal"
          >
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100" data-testid="text-explainer-video-title">{video.title}</h2>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
                data-testid="button-close-explainer-video"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={embedUrl}
                frameBorder="0"
                allowFullScreen
                allow="autoplay; fullscreen; picture-in-picture"
                className="absolute inset-0 w-full h-full"
                title={video.title}
                data-testid="iframe-explainer-video"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
